/**
 * JQ4-17 · US-CONFIG-003 — Hotel Exit/Re-entry Registration Page.
 *
 * Accessible via /hotel-exits/:ticketId. Allows valets to register a vehicle
 * exit and a re-entry for an active Hotel-niche ticket.
 *
 * Design:
 * - Shows current vehicle status (IN / OUT)
 * - "Register Exit" — calls POST /valet/tickets/:id/exits
 *   - Blocked (422 max_exits_exceeded) → shows "Maximum exits reached"
 *   - per_exit_fee policy → shows fee added in success state
 * - "Register Re-entry" — calls PATCH /valet/tickets/:id/exits/latest/re-entry
 *   - Only shown when vehicle is OUT (last exit has no reentryAt)
 * - Back navigation to the previous page
 */

import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY, type TicketExitResponse } from '@jq4race/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, formatCents } from '@jq4race/ui';
import { ArrowLeft, Car, CarFront, Loader2, X } from 'lucide-react';
import { useState } from 'react';

import { useRegisterExit, useRegisterReentry } from '../../hooks/use-ticket-exits.js';

interface HotelExitsPageProps {
  ticketId: string;
  ticketNumber: number;
  plate: string;
  /** Current vehicle status derived from caller — true if the most recent exit has no reentryAt */
  vehicleOut: boolean;
  /** Accumulated exit fee total so far — shown to the valet for awareness */
  exitFeeTotalCents?: number;
  /** Called when the user presses the back arrow */
  onBack: () => void;
  /** Called after a successful mutation so the caller can refresh its data */
  onMutationSuccess?: (result: TicketExitResponse) => void;
}

// TODO(JQ4-17 FIX-6): currency is hardcoded to DEFAULT_CURRENCY because neither
// TicketExitResponse nor the hotel-exits route search params carry the location
// currency. When the exit/ticket detail endpoint is extended to include
// currency, accept it as a prop and pass it through formatCents here.
function formatDollars(cents: number): string {
  return formatCents(cents, DEFAULT_CURRENCY);
}

export function HotelExitsPage({
  ticketId,
  ticketNumber,
  plate,
  vehicleOut: initialVehicleOut,
  exitFeeTotalCents = 0,
  onBack,
  onMutationSuccess,
}: HotelExitsPageProps) {
  const { t } = useTranslation('hotel');
  const [vehicleOut, setVehicleOut] = useState(initialVehicleOut);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const exitMutation = useRegisterExit(ticketId);
  const reentryMutation = useRegisterReentry(ticketId);

  const isWorking = exitMutation.isPending || reentryMutation.isPending;

  function handleExit() {
    setErrorMessage(null);
    setSuccessMessage(null);

    exitMutation.mutate(undefined, {
      onSuccess: (result) => {
        setVehicleOut(true);
        const fee = result.exitFeeCents ?? 0;
        setSuccessMessage(
          fee > 0
            ? t('messages.exitFeeApplied', { fee: formatDollars(fee) })
            : t('messages.exitRegistered'),
        );
        onMutationSuccess?.(result);
      },
      onError: (err) => {
        const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : null;
        if (code === 'max_exits_exceeded') {
          setErrorMessage(t('messages.maxExitsExceeded'));
        } else if (code === 'vehicle_out') {
          setErrorMessage(t('messages.vehicleAlreadyOut'));
        } else {
          setErrorMessage(t('messages.exitFailed'));
        }
      },
    });
  }

  function handleReentry() {
    setErrorMessage(null);
    setSuccessMessage(null);

    reentryMutation.mutate(undefined, {
      onSuccess: (result) => {
        setVehicleOut(false);
        setSuccessMessage(t('messages.reentryRegistered'));
        onMutationSuccess?.(result);
      },
      onError: () => {
        setErrorMessage(t('messages.reentryFailed'));
      },
    });
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={t('backAria')}
          onClick={onBack}
          className="flex size-8 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
        </button>
        <div>
          <p className="jq-h5 text-foreground font-semibold">{t('title')}</p>
          <p className="jq-body-sm text-muted-foreground font-mono">
            {t('ticketMeta', { number: ticketNumber, plate })}
          </p>
        </div>
      </div>

      {/* Vehicle status card */}
      <Card aria-label={t('vehicleStatusAria')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('currentStatus')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {vehicleOut ? (
              <CarFront className="size-5 text-warning" aria-hidden />
            ) : (
              <Car className="size-5 text-success" aria-hidden />
            )}
            <span className="jq-body font-medium text-foreground">
              {vehicleOut ? t('vehicleOut') : t('vehicleIn')}
            </span>
          </div>
          <Badge variant={vehicleOut ? 'warning' : 'success'}>
            {vehicleOut ? t('statusOut') : t('statusIn')}
          </Badge>
        </CardContent>
      </Card>

      {/* Accumulated exit fee summary */}
      {exitFeeTotalCents > 0 && (
        <Card aria-label={t('exitFeeSummaryAria')}>
          <CardContent className="pt-4 flex items-center justify-between gap-3">
            <span className="jq-body-sm text-muted-foreground">{t('exitFeesSoFar')}</span>
            <span className="jq-body-sm font-semibold text-foreground">
              {formatDollars(exitFeeTotalCents)}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Error message */}
      {errorMessage && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-sm border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          <span>{errorMessage}</span>
          <button
            type="button"
            aria-label={t('dismissErrorAria')}
            onClick={() => setErrorMessage(null)}
            className="shrink-0"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-sm border border-success/40 bg-success/10 px-4 py-3 text-sm text-success"
        >
          <span>{successMessage}</span>
          <button
            type="button"
            aria-label={t('dismissAria')}
            onClick={() => setSuccessMessage(null)}
            className="shrink-0"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {/* Register Exit — only when vehicle is IN */}
        {!vehicleOut && (
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={isWorking}
            onClick={handleExit}
            aria-label={t('registerExitAria')}
          >
            {exitMutation.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
            ) : (
              <CarFront className="mr-2 size-4" aria-hidden />
            )}
            {t('registerExit')}
          </Button>
        )}

        {/* Register Re-entry — only when vehicle is OUT */}
        {vehicleOut && (
          <Button
            type="button"
            size="lg"
            variant="outline"
            className="w-full"
            disabled={isWorking}
            onClick={handleReentry}
            aria-label={t('registerReentryAria')}
          >
            {reentryMutation.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
            ) : (
              <Car className="mr-2 size-4" aria-hidden />
            )}
            {t('registerReentry')}
          </Button>
        )}
      </div>
    </div>
  );
}
